import * as React from 'react';
import {
  ProgressBar as RACProgressBar,
  type ProgressBarProps as RACProgressBarProps,
} from 'react-aria-components';

import { cn } from '../lib/cn';

/**
 * Klaret Progress / ProgressBar (handoff §10.9).
 * Bar: 4px --tvl-inset track, accent fill, r-full.
 * Indeterminate: 30% segment translating at d-max loop.
 * Optional footnote % in tabular-nums.
 * No Radix dependency.
 */

const variantFill: Record<string, string> = {
  accent: 'bg-[var(--tvl-accent)]',
  success: 'bg-[var(--tvl-success)]',
  danger: 'bg-[var(--tvl-danger)]',
  info: 'bg-[var(--tvl-info)]',
  warning: 'bg-[var(--tvl-warning)]',
  // legacy alias kept for existing call sites
  orange: 'bg-[var(--tvl-accent)]',
};

const heightCls: Record<string, string> = {
  sm: 'h-1',
  md: 'h-1',   // Klaret: 4px track — sm/md both map to h-1 (4px)
  lg: 'h-1.5', // slight upsized variant
};

export interface ProgressProps
  extends Omit<RACProgressBarProps, 'value' | 'minValue' | 'maxValue' | 'isIndeterminate'> {
  value?: number;
  max?: number;
  /** 'accent' | 'success' | 'danger' | 'info' | 'warning' | 'orange' (legacy). Default: 'accent'. */
  variant?: keyof typeof variantFill;
  size?: 'sm' | 'md' | 'lg';
  /** Show percentage label below the bar. */
  showLabel?: boolean;
  isIndeterminate?: boolean;
}

export const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      className,
      value = 0,
      max = 100,
      variant = 'accent',
      size = 'md',
      showLabel = false,
      isIndeterminate = false,
      ...props
    },
    ref,
  ) => {
    const pct = isIndeterminate ? null : Math.min(100, Math.max(0, (value / max) * 100));
    const fill = variantFill[variant] ?? variantFill.accent;
    const h = heightCls[size] ?? heightCls.md;

    return (
      <RACProgressBar
        ref={ref}
        value={isIndeterminate ? undefined : value}
        minValue={0}
        maxValue={max}
        isIndeterminate={isIndeterminate}
        className={cn('flex w-full flex-col gap-1', className)}
        {...props}
      >
        {/* track */}
        <span
          className={cn(
            'relative w-full overflow-hidden rounded-[var(--tvl-r-full)] bg-[var(--tvl-inset)]',
            h,
          )}
        >
          {isIndeterminate ? (
            /* indeterminate: 30% segment translating */
            <span
              className={cn(
                'absolute inset-y-0 w-[30%] rounded-[var(--tvl-r-full)]',
                fill,
                'animate-[tvl-progress-indeterminate_var(--tvl-d-max)_linear_infinite]',
              )}
            />
          ) : (
            <span
              className={cn(
                'absolute inset-y-0 left-0 rounded-[var(--tvl-r-full)]',
                fill,
                'transition-[width] duration-[var(--tvl-d-base)] ease-[var(--tvl-ease-out)]',
              )}
              style={{ width: `${pct}%` }}
            />
          )}
        </span>

        {/* optional percentage label */}
        {showLabel && pct !== null ? (
          <span className="text-xs tabular-nums text-[var(--tvl-ink-3)]">
            {Math.round(pct)}%
          </span>
        ) : null}
      </RACProgressBar>
    );
  },
);
Progress.displayName = 'Progress';
